import { Container } from "../core/Container.js";
import { Control } from "../core/Control.js";
import type { Desktop } from "../core/Desktop.js";
import { Grid, type GridPlacement } from "../controls/Grid.js";
import { Page } from "../controls/Page.js";
import { StackPanel } from "../controls/StackPanel.js";
import { Window } from "../controls/Window.js";
import { ModernTheme } from "../themes/ModernTheme.js";
import type { Theme } from "../themes/Theme.js";
import {
  consoleWarnings,
  listNames,
  MarkupError,
  type WarningSink,
} from "./errors.js";
import { registerCoreElements } from "./elements/index.js";
import {
  addToControls,
  ElementRegistry,
  elements as globalElements,
  type Attrs,
  type AttrTable,
  type BuildContext,
  type ElementDefinition,
  type MarkupHandler,
  type MarkupViewLike,
} from "./registry.js";
import {
  applyAbsoluteSizing,
  coerce,
  LOADER_ATTRS,
  sizingOf,
  tableFor,
  type Sizing,
} from "./schema.js";
import { DECORATIONS } from "./decorations.js";
import { VariableStore, type VariableValue } from "./variables.js";
import { ArtControl } from "../art/ArtControl.js";
import { StaticValues, substituteTree, takeValues } from "./statics.js";
import { classesOf, StyleSheet, takeStyles } from "./styles.js";
import { isEventAttribute, eventNameOf, toPixels, type Length } from "./values.js";
import { parseMarkup, type XmlElement } from "./xml.js";
import { registerLibraryChild } from "../art/markup/library.js";

registerCoreElements();

export interface MarkupOptions {
  /** Functions the `on*` attributes name. A named handler that is missing is an error. */
  handlers?: Record<string, MarkupHandler>;
  /** Attach the root as soon as it is built: a Window is shown, a Page fills the desktop. */
  desktop?: Desktop;
  /**
   * Put the root in this container instead of on the desktop.
   *
   * For a document that is part of a larger UI rather than the whole of one — a pane, a tab, a
   * preview. The root fills the container unless it says otherwise, and the theme comes from
   * wherever the container is attached, so `em` and palette names resolve against the real one.
   */
  parent?: Container;
  /** A sheet shared with other documents, merged under any `<Styles>` in this one. */
  styles?: StyleSheet;
  /**
   * Values the caller supplies, overriding any the document declares for itself.
   *
   * The opposite way round from `styles`, and deliberately: a shared stylesheet is a house style
   * a document may want to refine, while a value passed here is the host parameterising this
   * particular load — a localized string, a tenant's product name, a setting read from
   * somewhere. Nobody passes one by accident, so whoever passes one means it.
   */
  values?: Readonly<Record<string, string>>;
  registry?: ElementRegistry;
  onWarning?: WarningSink;
  /**
   * The theme to resolve `em` and palette names against before there is a desktop.
   *
   * Only used for a fragment that is never shown; anything attached re-resolves against the
   * desktop's real theme.
   */
  theme?: Theme;
}

/** Something that had to wait until the tree was attached and laid out. */
interface Deferred {
  control: Control;
  definition: ElementDefinition<never>;
  attrs: Attrs;
}

/**
 * A loaded document: the control tree, plus the names to reach into it by.
 *
 * The names are the entire binding story, which is the decision the dialect rests on. Markup
 * describes structure; code does behavior. `view.get("title")` is a typed handle to one control
 * and `handlers` is where the verbs live, and between them there is nothing to keep in sync — no
 * expressions in attributes, no observable to forget to unsubscribe from, nothing that can be
 * stale. It is less than a binding engine on purpose.
 */
export class MarkupView implements MarkupViewLike {
  readonly root: Control;
  readonly handlers: Readonly<Record<string, MarkupHandler>>;

  #controls = new Map<string, Control>();
  #specs = new Map<string, unknown>();
  #desktop: Desktop | null = null;
  #onThemeChanged: (() => void) | null = null;
  /** Re-applied on a theme change: everything written in `em` is a function of the font size. */
  #emBindings: ((theme: Theme) => void)[] = [];

  #values = new StaticValues();
  /** The live values a host sets with `set`. */
  readonly variables = new VariableStore();

  constructor(root: Control, handlers: Record<string, MarkupHandler>) {
    this.root = root;
    this.handlers = handlers;
  }

  /**
   * A declared value, already resolved. Null when nothing declares it.
   *
   * For the settings a document declares that code also needs — a product name in a dialog
   * title, a page size a printer wants — so the two cannot drift apart by being written twice.
   */
  value(name: string): string | null {
    return this.#values.has(name) ? this.#values.resolve(`{${name}}`, { line: 0, col: 0 }) : null;
  }

  /** Every name the document declared. */
  valueNames(): string[] {
    return this.#values.names();
  }

  /** @internal */
  useValues(values: StaticValues): void {
    this.#values = values;
  }

  /** A control by `name`, or a `MarkupError` naming the ones that exist. */
  get<T extends Control = Control>(name: string): T {
    const found = this.#controls.get(name);
    if (!found) {
      throw new MarkupError(
        `no control is named "${name}"; this document has ${listNames([...this.#controls.keys()])}`,
        { line: 0, col: 0 },
      );
    }
    return found as T;
  }

  tryGet<T extends Control = Control>(name: string): T | null {
    return (this.#controls.get(name) as T | undefined) ?? null;
  }

  /**
   * A named thing that is data rather than a control: a menu item, a status panel, a tree node.
   *
   * Those are live objects their bar reads on every paint, so changing one and invalidating the
   * bar is how an application updates it — `view.spec<StatusPanelSpec>("statusText").text = "…"`.
   */
  spec<T = unknown>(name: string): T {
    const found = this.#specs.get(name);
    if (found === undefined) {
      throw new MarkupError(
        `nothing is named "${name}"; this document names ${listNames([...this.#specs.keys()])}`,
        { line: 0, col: 0 },
      );
    }
    return found as T;
  }

  names(): string[] {
    return [...this.#controls.keys()].sort();
  }

  /**
   * Set a variable the document declared in `<Variables>`; every property `<Link>`ed to it
   * follows, easing if the variable says so. This is the host's whole channel into a scene.
   */
  set(name: string, value: VariableValue, immediate = false): void {
    this.variables.set(name, value, immediate);
  }

  /** A variable's current value - what was last set, not the eased value on its way there. */
  variable(name: string): VariableValue | undefined {
    return this.variables.get(name);
  }

  /**
   * Play the document's arrival again: every `load` and `reveal` motion back to waiting, and every
   * piece of art's clock back to zero so it draws itself in again. For a screen shown a second
   * time, which should arrive the way it did the first.
   */
  replay(): void {
    const walk = (control: Control): void => {
      if (control.hasMotion) control.motion.replay();
      if (control instanceof ArtControl) control.restart();
      if (control instanceof Container) for (const child of control.controls) walk(child);
    };
    walk(this.root);
  }

  /** Attach the root to a desktop and run everything that had to wait for one. */
  show(desktop: Desktop): this {
    if (this.#desktop === desktop) return this;
    const root = this.root;
    if (root instanceof Window) desktop.addWindow(root);
    else if (root instanceof Page) root.show(desktop);
    else {
      throw new MarkupError(
        `only a <Window> or a <Page> can be shown on its own; ` +
          `add this <${root.constructor.name}> to a container yourself`,
        { line: 0, col: 0 },
      );
    }
    this.#attached(desktop);
    return this;
  }

  /** Stop following the desktop, and take the root off it. */
  dispose(): void {
    const desktop = this.#desktop;
    this.variables.detach();
    if (desktop && this.#onThemeChanged) desktop.off("themechanged", this.#onThemeChanged);
    this.#onThemeChanged = null;
    this.#desktop = null;
    const root = this.root;
    if (root instanceof Window) root.close();
    else root.parent?.controls.remove(root);
    this.#controls.clear();
    this.#specs.clear();
  }

  /** @internal */
  nameControl(name: string, control: Control): void {
    this.#controls.set(name, control);
  }

  /** @internal */
  nameSpec(name: string, spec: unknown): void {
    this.#specs.set(name, spec);
  }

  /** @internal */
  addEmBinding(apply: (theme: Theme) => void): void {
    this.#emBindings.push(apply);
  }

  /**
   * Bookkeeping once the root is on a desktop.
   *
   * A theme change moves every `em` in the document, because `em` is the theme's font size, so
   * the sizes written in it are re-applied rather than frozen at load. That is what makes a
   * layout follow a theme swap the same way a control's own metrics do.
   * @internal
   */
  attached(desktop: Desktop): void {
    this.#attached(desktop);
  }

  #attached(desktop: Desktop): void {
    this.#desktop = desktop;
    this.variables.attach(desktop);
    if (this.#emBindings.length === 0 || this.#onThemeChanged) return;
    this.#onThemeChanged = (): void => {
      const theme = desktop.theme;
      for (const binding of this.#emBindings) binding(theme);
      if (this.root instanceof Container) this.root.layout();
    };
    desktop.on("themechanged", this.#onThemeChanged);
  }
}

/**
 * Build a control tree from markup.
 *
 * The order is the whole of it, and every step of it exists because of something that bites when
 * it is done in a different one:
 *
 * 1. Parse, and lift `<Styles>` out of the root.
 * 2. Build the tree **detached**, depth-first, each control constructed with every attribute it
 *    can take through its constructor — several controls read `items`, `checked` and `text` only
 *    there.
 * 3. Attach the root to the desktop. This is the moment there is a theme, and so the first
 *    moment anything can measure text; `notifyEnvironmentChanged` walks children first and then
 *    lays out, so the whole tree measures in one pass rather than N.
 * 4. Run what could not be known before: a splitter position that is a fraction of a width, a
 *    selected tab, a window centered in a work area it has only just joined.
 *
 * A window built before it is shown has placeholder bounds — the framework says so in several
 * places — and this is the ordering that makes that invisible to whoever writes the markup.
 */
export function loadMarkup(xml: string, options: MarkupOptions = {}): MarkupView {
  return buildDocument(parseMarkup(xml), options);
}

/** The same as `loadMarkup`, for a document that has already been parsed. */
export function buildDocument(root: XmlElement, options: MarkupOptions = {}): MarkupView {
  const registry = options.registry ?? globalElements;
  const warn = options.onWarning ?? consoleWarnings;
  const handlers = options.handlers ?? {};
  const parent = options.parent ?? null;
  const desktop = options.desktop ?? parent?.desktop ?? null;
  const theme = desktop?.theme ?? options.theme ?? new ModernTheme("light");

  // Values first: everything below, including the style attributes, may reference them.
  const values = takeValues(root, new StaticValues());
  for (const [name, text] of Object.entries(options.values ?? {})) values.set(name, text);
  substituteTree(root, values);

  // A shared sheet sits under the document's own, so a document can override one rule of a house
  // style without restating the rest — and so loading a document never edits the caller's sheet.
  const own = takeStyles(root, new StyleSheet());
  // Definitions and looks belong to the whole application, wherever in a document they are written.
  takeArtDefinitions(root);
  const sheets = options.styles ? [options.styles, own] : [own];

  const deferred: Deferred[] = [];
  /** `ctx.later` work: what needs the attached, laid-out tree - a pin needs both ends placed. */
  const laterWork: (() => void)[] = [];
  /** Everything written in `em`, ready to be resolved again against a new theme. */
  const emApplies: ((theme: Theme) => void)[] = [];
  const view = new MarkupView(null as unknown as Control, handlers);

  const bindEm = (length: Length, apply: (pixels: number) => void): number => {
    if (length.kind === "em") emApplies.push((next) => apply(toPixels(length, next.metrics)));
    const px = toPixels(length, theme.metrics);
    apply(px);
    return px;
  };

  const ctx: BuildContext = {
    theme,
    desktop,
    view,
    warn,
    nameSpec: (name, spec) => view.nameSpec(name, spec),
    build: (element, parent) => build(element, parent),
    attributesOf: (element, table) => resolve(element, table),
    bindEm,
    bindTheme: (apply) => {
      emApplies.push(apply);
      apply(theme);
    },
    variables: view.variables,
    later: (fn) => {
      laterWork.push(fn);
    },
    handler: (name, at) => {
      const found = handlers[name];
      if (!found) {
        throw new MarkupError(
          `no handler named "${name}" was passed to loadMarkup; it has ${listNames(Object.keys(handlers))}`,
          at,
        );
      }
      return found;
    },
  };

  /** Style attributes first, then the element's own, then coerce the lot. */
  function resolve(element: XmlElement, table: AttrTable): Attrs {
    const attrs: Attrs = {};
    const definition =
      registry.lookup(element.name) ?? ({ tag: element.name } as ElementDefinition<never>);
    const classes = classesOf(element);
    const styled = sheets.flatMap((sheet) => sheet.attributesFor(definition, classes));
    const resolvedStyles = styled.map((a) => ({ ...a, value: values.resolve(a.value, a) }));
    for (const attribute of [...resolvedStyles, ...element.attributes]) {
      if (isEventAttribute(attribute.name)) continue;
      // "*" takes any name the table does not: a drawing's parameters, which nobody can list.
      const type = table[attribute.name] ?? table["*"];
      if (type === undefined) {
        warn({
          message: `<${element.name}> has no "${attribute.name}" attribute`,
          line: attribute.line,
          col: attribute.col,
        });
        continue;
      }
      const value = coerce(attribute, type, { theme, warn });
      if (value !== undefined) attrs[attribute.name] = value;
    }
    return attrs;
  }

  /** Everything universal: names, events, placement, sizing, children. */
  function build(element: XmlElement, parent: Control | null): Control {
    const definition = registry.require(element);
    const table = tableFor(definition.base, definition.attributes);
    const attrs = resolve(element, table);

    // On an element, `align` is shorthand for both of its own axes, and loses to either written
    // on its own. The panel-level default for a panel's *children* is `alignItems`, which is what
    // ends up as the constructor's `align` — two different questions that would otherwise share
    // a word and be wrong half the time.
    const both = attrs["align"];
    delete attrs["align"];
    if (both !== undefined) {
      attrs["hAlign"] ??= both;
      attrs["vAlign"] ??= both;
    }
    if (attrs["fill"] === true) attrs["dock"] ??= "fill";
    if (attrs["gap"] !== undefined) attrs["spacing"] = attrs["gap"];
    if (attrs["alignItems"] !== undefined) attrs["align"] = attrs["alignItems"];

    const sizing = sizingOf(attrs);
    applyPlacementDefault(element, attrs, parent, definition, sizing);

    const options: Attrs = {};
    for (const [key, value] of Object.entries(attrs)) {
      if (!LOADER_ATTRS.has(key)) options[key] = value;
    }
    if (element.text && options["text"] === undefined && !definition.text) {
      options["text"] = element.text;
    }

    const control = definition.create(options, ctx, element);
    resolved.set(control, attrs);
    if (typeof attrs["shape"] === "string") (control as Control).shape = attrs["shape"];
    if (definition.text && element.text) definition.text(control as never, element.text, ctx);

    const name = attrs["name"] as string | undefined;
    if (name) view.nameControl(name, control);

    bindEvents(control, element);
    applySizing(control, sizing, parent);
    if (definition.afterAttach) deferred.push({ control, definition, attrs });

    buildChildren(control, element, definition);
    return control;
  }

  /**
   * The default that removes the most attributes: an only child fills its parent.
   *
   * A container with one child and no docking almost always means "you are the content". Saying
   * so here rather than making everyone write `fill` is what lets the notes example carry no
   * placement at all on its biggest element.
   */
  function applyPlacementDefault(
    element: XmlElement,
    attrs: Attrs,
    parent: Control | null,
    definition: ElementDefinition<never>,
    sizing: Sizing,
  ): void {
    void element;
    void definition;
    void sizing;
    if (!parent || !(parent instanceof Container)) return;
    const parentDefinition = parentDefinitionOf(parent);
    if (!parentDefinition?.docksChildren) return;
    if (parent instanceof Grid || parent instanceof StackPanel) return;
    const placed =
      attrs["dock"] !== undefined ||
      attrs["hAlign"] !== undefined ||
      attrs["vAlign"] !== undefined ||
      attrs["x"] !== undefined ||
      attrs["y"] !== undefined;
    if (!placed) attrs["dock"] = "fill";
  }

  const definitions = new WeakMap<Control, ElementDefinition<never>>();
  /**
   * Each control's attributes as they were read.
   *
   * Kept because grid placement is decided by the *parent* — after the child exists and is in
   * the grid — and re-reading the element there would resolve it against a table holding only
   * the cell attributes, warning about every real one the child had.
   */
  const resolved = new WeakMap<Control, Attrs>();
  function parentDefinitionOf(parent: Control): ElementDefinition<never> | undefined {
    return definitions.get(parent);
  }

  function bindEvents(control: Control, element: XmlElement): void {
    for (const attribute of element.attributes) {
      if (!isEventAttribute(attribute.name)) continue;
      const handler = ctx.handler(attribute.value, element);
      const event = eventNameOf(attribute.name);
      (control as unknown as { on(e: string, h: (a: unknown) => void): void }).on(event, (args) =>
        handler(args as never, control, view),
      );
    }
  }

  /**
   * Turn a sizing into whatever this particular parent understands.
   *
   * A star is a weight in a stack and a stretch anywhere else; a percentage is a fraction in a
   * stack and has no meaning outside one. Asking the parent rather than the control is the only
   * way round the fact that `Control` has no attached properties — `Grid` solved the same
   * problem the same way, with placement kept beside the panel.
   */
  function applySizing(control: Control, sizing: Sizing, parent: Control | null): void {
    applyAbsoluteSizing(control, sizing, theme);
    for (const axis of ["width", "height"] as const) {
      const length = sizing[axis];
      if (length.kind === "em") {
        bindEm(length, (px) => {
          if (axis === "width") control.setSize(px, control.height);
          else control.setSize(control.width, px);
        });
      }
      if (length.kind !== "star" && length.kind !== "percent") continue;

      const main =
        parent instanceof StackPanel
          ? (parent.orientation === "horizontal") === (axis === "width")
          : false;
      if (main && parent instanceof StackPanel) {
        parent.setPlacement(
          control,
          length.kind === "star" ? { fill: length.value } : { fraction: length.value },
        );
        continue;
      }
      if (length.kind === "star") {
        if (axis === "width") control.hAlign = "stretch";
        else control.vAlign = "stretch";
        continue;
      }
      // A percentage of a container that does not divide anything up has no reading; `auto` is
      // the nearest honest one, and the warning says so rather than guessing.
      if (axis === "width") control.autoWidth = true;
      else control.autoHeight = true;
    }
  }

  function buildChildren(
    control: Control,
    element: XmlElement,
    definition: ElementDefinition<never>,
  ): void {
    definitions.set(control, definition);
    if (definition.content) {
      const content: XmlElement[] = [];
      for (const child of element.children) {
        const decoration = DECORATIONS[child.name];
        if (decoration && !definition.childSpecs?.[child.name]) decoration(child, ctx, control);
        else content.push(child);
      }
      definition.content(control as never, content, ctx);
      return;
    }
    for (const child of element.children) {
      const asSpec = definition.childSpecs?.[child.name];
      if (asSpec) {
        asSpec(child, ctx, control as never);
        continue;
      }
      // After the element's own: a control whose vocabulary already has a `<Flare>` of its own
      // meaning keeps it.
      const decoration = DECORATIONS[child.name];
      if (decoration) {
        decoration(child, ctx, control);
        continue;
      }
      const built = build(child, control);
      if (definition.addChild) definition.addChild(control as never, built, child, ctx);
      else addToControls(control, built);
      applyGridPlacement(control, built);
    }
  }

  /** `row`/`col` on any child of a grid, read after the child is in the grid. */
  function applyGridPlacement(parent: Control, child: Control): void {
    if (!(parent instanceof Grid)) return;
    const cell = resolved.get(child) ?? {};
    const placement: GridPlacement = {};
    const row = cell["row"] as number | undefined;
    const column = (cell["col"] ?? cell["column"]) as number | undefined;
    const rowSpan = cell["rowSpan"] as number | undefined;
    const columnSpan = (cell["colSpan"] ?? cell["columnSpan"]) as number | undefined;
    if (row !== undefined) placement.row = row;
    if (column !== undefined) placement.column = column;
    if (rowSpan !== undefined) placement.rowSpan = rowSpan;
    if (columnSpan !== undefined) placement.columnSpan = columnSpan;
    if (child.hAlign) placement.hAlign = child.hAlign;
    if (child.vAlign) placement.vAlign = child.vAlign;
    if (Object.keys(placement).length > 0) parent.setPlacement(child, placement);
  }

  const built = build(root, null);
  (view as unknown as { root: Control }).root = built;
  view.variables.resolve();
  view.useValues(values);

  // Everything written in `em` is a function of the theme's font size, so it is re-applied on a
  // theme change rather than baked in at load.
  if (emApplies.length > 0) {
    view.addEmBinding((next) => {
      for (const apply of emApplies) apply(next);
    });
  }

  if (parent) {
    // A document loaded into a container is content, so it fills the container the same way an
    // only child inside the document would — and adding it is what gives it a theme to measure
    // with, so this has to happen before the deferred work below.
    if (built.dock === "none" && built.hAlign === null && built.vAlign === null) {
      built.dock = "fill";
    }
    parent.controls.add(built);
    if (desktop) view.attached(desktop);
  } else if (desktop) {
    if (built instanceof Window) desktop.addWindow(built);
    else if (built instanceof Page) built.show(desktop);
    view.attached(desktop);
  }

  for (const item of deferred) item.definition.afterAttach?.(item.control as never, item.attrs, ctx);
  if (built instanceof Container) built.layout();
  if (desktop) for (const fn of laterWork) fn();

  return view;
}

/** Lift every `<Define>` and `<Look>` out of the tree and register it, before anything is built. */
function takeArtDefinitions(element: XmlElement): void {
  for (let i = element.children.length - 1; i >= 0; i--) {
    const child = element.children[i]!;
    if (child.name === "Define" || child.name === "Look") {
      registerLibraryChild(child);
      element.children.splice(i, 1);
    } else {
      takeArtDefinitions(child);
    }
  }
}

export { parseMarkup };
